import React, { useEffect, useState, useMemo } from "react";
import { api } from "../scripts/api";

function fmt(min) {
  if (min == null) return "—";
  if (min < 60) return `${min}m`;
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  return m ? `${h}h ${m}m` : `${h}h`;
}

function fmtDate(s) {
  if (!s) return "—";
  return s.slice(0, 16).replace("T", " ");
}

function StatCard({ label, value, sub, color }) {
  return (
    <div className="card" style={{ textAlign: "center", padding: "20px 16px" }}>
      <div style={{ fontSize: 32, fontWeight: 900, color: color || "var(--fg)" }}>{value}</div>
      <div style={{ fontWeight: 700, marginTop: 4 }}>{label}</div>
      {sub && <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function Bar({ value, max, color = "#3b82f6" }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div style={{ height: 8, background: "var(--card-border)", borderRadius: 99, overflow: "hidden" }}>
      <div style={{ height: "100%", width: `${pct}%`, background: color, borderRadius: 99, transition: "width 0.4s" }} />
    </div>
  );
}

export default function PdiReportsPage() {
  const [data,    setData]    = useState(null);
  const [busy,    setBusy]    = useState(false);
  const [msg,     setMsg]     = useState("");
  const [tab,     setTab]     = useState("overview"); // overview | sessions | tech
  const [search,  setSearch]  = useState("");
  const [filterStatus, setFilterStatus] = useState("all");

  const load = async () => {
    setBusy(true);
    setMsg("");
    try {
      const { data: d } = await api.get("/pdi/reports");
      setData(d);
    } catch (e) {
      setMsg(e?.response?.data?.error || "Failed to load reports");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filteredSessions = useMemo(() => {
    if (!data?.sessions) return [];
    let rows = data.sessions;
    if (filterStatus !== "all") rows = rows.filter(s => s.status === filterStatus);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      rows = rows.filter(s =>
        (s.rowo || "").toLowerCase().includes(q) ||
        (s.customer || "").toLowerCase().includes(q) ||
        (s.template || "").toLowerCase().includes(q) ||
        (s.tech_initials || "").toLowerCase().includes(q) ||
        (s.techs || "").toLowerCase().includes(q) ||
        (s.stock_po || "").toLowerCase().includes(q)
      );
    }
    return rows;
  }, [data, search, filterStatus]);

  const maxDuration = useMemo(() => {
    if (!data?.sessions) return 1;
    return Math.max(1, ...data.sessions.map(s => s.duration_minutes || 0));
  }, [data]);

  if (busy && !data) return <div className="container"><div className="muted" style={{ marginTop: 32 }}>Loading reports…</div></div>;
  if (msg)           return <div className="container"><div style={{ marginTop: 32, color: "#dc2626" }}>{msg}</div></div>;
  if (!data)         return null;

  const { summary, by_template, by_tech } = data;
  const maxTemplateTotal = Math.max(1, ...by_template.map(t => t.total));
  const maxTechSteps     = Math.max(1, ...by_tech.map(t => t.steps_completed));

  const TABS = [
    { id: "overview", label: "Overview" },
    { id: "sessions", label: `Sessions (${data.sessions.length})` },
    { id: "tech",     label: `By Tech (${by_tech.length})` },
  ];

  return (
    <div className="container">
      {/* Header */}
      <div className="row" style={{ justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontWeight: 900 }}>PDI Reports</h2>
        <button className="btn-outline" onClick={load} disabled={busy}>Refresh</button>
      </div>

      {/* Tabs */}
      <div className="row" style={{ gap: 4, marginBottom: 20, flexWrap: "wrap" }}>
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={tab === t.id ? "btn" : "btn-outline"}
            style={{ fontSize: 14 }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ── OVERVIEW ── */}
      {tab === "overview" && (
        <div style={{ display: "grid", gap: 20 }}>
          {/* KPI row */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 12 }}>
            <StatCard label="Total Sessions"    value={summary.total_sessions} />
            <StatCard label="Completed"         value={summary.completed}       color="#16a34a" />
            <StatCard label="In Progress"       value={summary.in_progress}     color="#2563eb" />
            <StatCard label="Avg Duration"      value={fmt(summary.avg_duration_min)}     sub="completed sessions" />
            <StatCard label="Median Duration"   value={fmt(summary.median_duration_min)}  sub="completed sessions" />
            <StatCard label="Total Deficiencies" value={summary.total_deficiencies} color="#d97706" />
            <StatCard label="Open Deficiencies"  value={summary.open_deficiencies}  color={summary.open_deficiencies > 0 ? "#dc2626" : "#16a34a"} />
          </div>

          {/* By template */}
          {by_template.length > 0 && (
            <div className="card">
              <div style={{ fontWeight: 900, fontSize: 16, marginBottom: 14 }}>By Template</div>
              <div style={{ display: "grid", gap: 14 }}>
                {by_template.map(t => (
                  <div key={t.template}>
                    <div className="row" style={{ justifyContent: "space-between", marginBottom: 4, flexWrap: "wrap", gap: 4 }}>
                      <span style={{ fontWeight: 700 }}>{t.template}</span>
                      <span className="muted" style={{ fontSize: 13 }}>
                        {t.completed}/{t.total} completed
                        {t.avg_duration != null && ` · avg ${fmt(t.avg_duration)}`}
                        {t.total_defs > 0 && ` · ${t.total_defs} defs (${t.open_defs} open)`}
                      </span>
                    </div>
                    <Bar value={t.total} max={maxTemplateTotal} color="#3b82f6" />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* By tech */}
          {by_tech.length > 0 && (
            <div className="card">
              <div style={{ fontWeight: 900, fontSize: 16, marginBottom: 14 }}>By Technician</div>
              <div style={{ display: "grid", gap: 14 }}>
                {by_tech.map(t => (
                  <div key={t.tech_name}>
                    <div className="row" style={{ justifyContent: "space-between", marginBottom: 4, flexWrap: "wrap", gap: 4 }}>
                      <span style={{ fontWeight: 700 }}>{t.tech_name}</span>
                      <span className="muted" style={{ fontSize: 13 }}>
                        {t.steps_completed} steps · {t.sessions_worked} sessions · last active {fmtDate(t.last_activity)}
                      </span>
                    </div>
                    <Bar value={t.steps_completed} max={maxTechSteps} color="#7c3aed" />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── SESSIONS ── */}
      {tab === "sessions" && (
        <div style={{ display: "grid", gap: 12 }}>
          {/* Filters */}
          <div className="row" style={{ gap: 10, flexWrap: "wrap" }}>
            <input
              className="input"
              placeholder="Search unit, customer, template, tech…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ minWidth: 240, flex: 1 }}
            />
            <select
              className="input"
              value={filterStatus}
              onChange={e => setFilterStatus(e.target.value)}
              style={{ width: "auto" }}
            >
              <option value="all">All statuses</option>
              <option value="completed">Completed</option>
              <option value="in_progress">In Progress</option>
            </select>
          </div>

          <div className="muted" style={{ fontSize: 13 }}>{filteredSessions.length} sessions</div>

          {/* Table */}
          <div className="card" style={{ padding: 0, overflow: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: "1px solid var(--card-border)" }}>
                  {["Unit", "Customer", "Template", "Tech", "Started", "Completed", "Duration", "Steps", "Defs (open)"].map(h => (
                    <th key={h} style={{ padding: "10px 14px", textAlign: "left", fontWeight: 700, whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredSessions.map(s => (
                  <tr key={s.id} style={{ borderBottom: "1px solid var(--card-border)" }}>
                    <td style={{ padding: "10px 14px", fontWeight: 700, whiteSpace: "nowrap" }}>
                      {s.rowo || s.stock_po || `#${s.id}`}
                    </td>
                    <td style={{ padding: "10px 14px" }}>{s.customer || "—"}</td>
                    <td style={{ padding: "10px 14px" }}>{s.template || "—"}</td>
                    <td style={{ padding: "10px 14px" }}>{s.techs || s.tech_initials || "—"}</td>
                    <td style={{ padding: "10px 14px", whiteSpace: "nowrap" }}>{fmtDate(s.started_at)}</td>
                    <td style={{ padding: "10px 14px", whiteSpace: "nowrap" }}>{fmtDate(s.completed_at)}</td>
                    <td style={{ padding: "10px 14px", whiteSpace: "nowrap" }}>
                      {s.duration_minutes != null ? (
                        <span style={{ color: s.duration_minutes > 120 ? "#dc2626" : s.duration_minutes > 60 ? "#d97706" : "#16a34a", fontWeight: 700 }}>
                          {fmt(s.duration_minutes)}
                        </span>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    <td style={{ padding: "10px 14px", textAlign: "center" }}>{s.steps_answered}</td>
                    <td style={{ padding: "10px 14px", textAlign: "center" }}>
                      {s.total_defs > 0 ? (
                        <span style={{ color: s.open_defs > 0 ? "#dc2626" : "#16a34a", fontWeight: 700 }}>
                          {s.total_defs} ({s.open_defs})
                        </span>
                      ) : "—"}
                    </td>
                  </tr>
                ))}
                {filteredSessions.length === 0 && (
                  <tr>
                    <td colSpan={9} style={{ padding: "24px", textAlign: "center" }} className="muted">No sessions match.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── BY TECH ── */}
      {tab === "tech" && (
        <div style={{ display: "grid", gap: 12 }}>
          {by_tech.length === 0 && (
            <div className="muted">No technician data yet. Tech names are recorded when steps are saved with a tech selected.</div>
          )}
          {by_tech.map(t => (
            <div key={t.tech_name} className="card">
              <div className="row" style={{ justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 8 }}>
                <div>
                  <div style={{ fontWeight: 900, fontSize: 18 }}>{t.tech_name}</div>
                  <div className="muted" style={{ fontSize: 13, marginTop: 2 }}>
                    First: {fmtDate(t.first_activity)} · Last: {fmtDate(t.last_activity)}
                  </div>
                </div>
                <div className="row" style={{ gap: 16 }}>
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontWeight: 900, fontSize: 22 }}>{t.sessions_worked}</div>
                    <div className="muted" style={{ fontSize: 12 }}>sessions</div>
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontWeight: 900, fontSize: 22 }}>{t.steps_completed}</div>
                    <div className="muted" style={{ fontSize: 12 }}>steps</div>
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontWeight: 900, fontSize: 22 }}>
                      {t.sessions_worked > 0 ? Math.round(t.steps_completed / t.sessions_worked) : 0}
                    </div>
                    <div className="muted" style={{ fontSize: 12 }}>steps/session</div>
                  </div>
                </div>
              </div>
              <div style={{ marginTop: 12 }}>
                <Bar value={t.steps_completed} max={maxTechSteps} color="#7c3aed" />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
